import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';
import type {
    CareerAspiration,
    CareerAspirationUpdateRequest,
    GrowthBlocker,
} from '@/types/career-growth';

import {
    ACCEPTS_NEW_RESPONSIBILITIES_VALUES,
    DEPARTURE_RISKS,
    DEVELOPMENT_PATHS,
    GROWTH_BLOCKERS,
    IMPORTANCE_LEVEL_MAX,
    MOTIVATION_FACTORS,
    NEW_ROLE_DEVELOPMENT_PATHS,
    SUPPORT_NEEDS,
} from '@/types/career-growth';

/**
 * The shape and the rules of the "Aspirations de carrière" form, shared by the editor modal and
 * its tests. Every answer is optional (a meeting rarely covers everything at once); the rules are
 * the bounds the API enforces, and that picking "Other" requires saying what that support is.
 * Messages are translation keys, resolved where the field is rendered.
 */

const TOO_LONG_MESSAGE: TranslationKey = 'performanceProfile.growth.aspiration.form.tooLong';

/** The column widths of the API: the inputs cap the typing at the same bounds. */
export const DESIRED_POSITION_MAX_LENGTH = 200;
export const ALIGNMENT_NOTES_MAX_LENGTH = 4000;
export const SHORT_NOTES_MAX_LENGTH = 2000;

/** A `SelectFilled` value is a string, so "no profile" travels as the empty option. */
export const NO_PROFILE_VALUE = '';

const boundedText = (max: number) => z.string().trim().max(max, { message: TOO_LONG_MESSAGE });

const OTHER_SUPPORT_REQUIRED_MESSAGE: TranslationKey =
    'performanceProfile.growth.aspiration.form.supportNeedsOtherRequired';

const careerAspirationFields = z.object({
    developmentPath: z.enum(DEVELOPMENT_PATHS).nullable(),
    desiredPositionTitle: boundedText(DESIRED_POSITION_MAX_LENGTH),
    desiredCompetencyProfileId: z.string(),
    importanceLevel: z.number().int().min(1).max(IMPORTANCE_LEVEL_MAX).nullable(),
    acceptsNewResponsibilities: z.enum(ACCEPTS_NEW_RESPONSIBILITIES_VALUES).nullable(),
    timeAlignmentNotes: boundedText(ALIGNMENT_NOTES_MAX_LENGTH),
    motivationFactors: z.array(z.enum(MOTIVATION_FACTORS)),
    supportNeeds: z.array(z.enum(SUPPORT_NEEDS)),
    hasOtherSupport: z.boolean(),
    supportNeedsOther: boundedText(SHORT_NOTES_MAX_LENGTH),
    growthBlockers: z.array(z.enum(GROWTH_BLOCKERS)),
    blockersNotes: boundedText(SHORT_NOTES_MAX_LENGTH),
    departureRisk: z.enum(DEPARTURE_RISKS).nullable(),
});

/** Picking "Other" asks what that support is: an empty text would otherwise be dropped unseen. */
export const careerAspirationSchema = careerAspirationFields.superRefine((values, context) => {
    if (values.hasOtherSupport && !values.supportNeedsOther.trim()) {
        context.addIssue({
            code: 'custom',
            path: ['supportNeedsOther'],
            message: OTHER_SUPPORT_REQUIRED_MESSAGE,
        });
    }
});

export type CareerAspirationFormValues = z.infer<typeof careerAspirationSchema>;

export const isNewRolePath = (path: CareerAspirationFormValues['developmentPath']) =>
    path !== null && NEW_ROLE_DEVELOPMENT_PATHS.has(path);

export const NO_BLOCKER: GrowthBlocker = 'NoneParticular';

// The server refuses "no particular blocker" beside a real one: the real ones win.
const consistentBlockers = (blockers: GrowthBlocker[]) =>
    blockers.length > 1 ? blockers.filter((blocker) => blocker !== NO_BLOCKER) : blockers;

/** The form's values for an existing aspiration, or a blank sheet. */
export const toFormValues = (aspiration: CareerAspiration | null): CareerAspirationFormValues => ({
    developmentPath: aspiration?.developmentPath ?? null,
    desiredPositionTitle: aspiration?.desiredPositionTitle ?? '',
    desiredCompetencyProfileId: aspiration?.desiredCompetencyProfileId ?? NO_PROFILE_VALUE,
    importanceLevel: aspiration?.importanceLevel ?? null,
    acceptsNewResponsibilities: aspiration?.acceptsNewResponsibilities ?? null,
    timeAlignmentNotes: aspiration?.timeAlignmentNotes ?? '',
    motivationFactors: aspiration?.motivationFactors ?? [],
    supportNeeds: aspiration?.supportNeeds ?? [],
    hasOtherSupport: !!aspiration?.supportNeedsOther?.trim(),
    supportNeedsOther: aspiration?.supportNeedsOther ?? '',
    growthBlockers: consistentBlockers(aspiration?.growthBlockers ?? []),
    blockersNotes: aspiration?.blockersNotes ?? '',
    departureRisk: aspiration?.departureRisk ?? null,
});

const orNull = (text: string) => (text.trim() ? text.trim() : null);

const ANSWERS: readonly (keyof CareerAspirationUpdateRequest)[] = [
    'desiredPositionTitle',
    'desiredCompetencyProfileId',
    'importanceLevel',
    'acceptsNewResponsibilities',
    'timeAlignmentNotes',
    'motivationFactors',
    'developmentPath',
    'supportNeeds',
    'supportNeedsOther',
    'growthBlockers',
    'blockersNotes',
    'departureRisk',
];

export const hasAnyAnswer = (request: CareerAspirationUpdateRequest) =>
    ANSWERS.some((field) => {
        const answer = request[field];
        return Array.isArray(answer) ? answer.length > 0 : answer !== null;
    });

/**
 * The answers the API expects, each travelling exactly while the form asks it. The competency
 * profile is asked on every path, so it travels as soon as a path is picked; the rest of the "new
 * role" answers only exist for the paths that aim at one. The other support's text travels only
 * while "Other" is picked; the form also empties it when "Other" is unpicked.
 */
export const toCareerAspirationRequest = (
    values: CareerAspirationFormValues,
): CareerAspirationUpdateRequest => {
    const newRole = isNewRolePath(values.developmentPath);

    return {
        developmentPath: values.developmentPath,
        desiredPositionTitle: newRole ? orNull(values.desiredPositionTitle) : null,
        desiredCompetencyProfileId: values.developmentPath
            ? orNull(values.desiredCompetencyProfileId)
            : null,
        importanceLevel: newRole ? values.importanceLevel : null,
        acceptsNewResponsibilities: newRole ? values.acceptsNewResponsibilities : null,
        timeAlignmentNotes: orNull(values.timeAlignmentNotes),
        motivationFactors: newRole ? values.motivationFactors : [],
        supportNeeds: values.supportNeeds,
        supportNeedsOther: values.hasOtherSupport ? orNull(values.supportNeedsOther) : null,
        growthBlockers: values.growthBlockers,
        blockersNotes: orNull(values.blockersNotes),
        departureRisk: values.departureRisk,
    };
};
